import type { JSX } from 'react';
import { DaySegment } from '@api/ShiftTypeService';
import type { DailyCoverDemand } from '@api/ShiftGeneratorTaskService';

/**
 * Minimal task shape consumed by the task-status helpers below
 * (`taskStuck`, `isTaskReadOnly`, `isTaskInProcessState`). Accepts the full
 * `ShiftGeneratorTask` DTO as well as view-level task types.
 */
type TaskStatusSource =
    | {
          status?: string | null;
          runner?: { status?: string | null; started?: string | null } | null;
      }
    | null
    | undefined;
import type { DynomiteError } from '../dynomite/ErrorMessageUtil';
import { differenceInMilliseconds, isValid, parse, parseISO } from 'date-fns';
import { taskTimeoutMinutes } from 'src/api/dynoape';

/**
 * Helper function for shortening a string
 * @param str
 * @param size
 * @returns {string|*}
 */
export const shortenStr = (
    str: string | null | undefined,
    size = 10,
): string => {
    if (!str) {
        return '';
    }
    if (!size || size < 1) {
        return str;
    }
    return str.length > size + 1 ? str.slice(0, size).concat('.') : str;
};

const debounce = <T extends (...args: never[]) => void>(
    func: T,
    wait: number,
    immediate = false,
): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
        const callNow = immediate && !timeout;

        const later = (): void => {
            timeout = null;
            if (!immediate) {
                func.apply(this, args);
            }
        };

        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(later, wait);

        if (callNow) {
            func.apply(this, args);
        }
    };
};

const taskStuck = (task: TaskStatusSource): boolean => {
    const started = task?.runner?.started;
    if (!started) {
        return false;
    }

    // The API sends 'yyyy-MM-dd HH:mm:ss', but moment also silently
    // accepted ISO strings, so keep both paths for identical behavior.
    const parsedWithFormat = parse(started, 'yyyy-MM-dd HH:mm:ss', new Date());
    const taskStarted = isValid(parsedWithFormat)
        ? parsedWithFormat
        : parseISO(started);

    if (!isValid(taskStarted)) {
        return false;
    }

    const dur = differenceInMilliseconds(new Date(), taskStarted) / 60000;
    return dur >= Math.floor(taskTimeoutMinutes as number) + 1;
};

const norwegianShiftNames = (shiftCode: string): string | undefined => {
    if (shiftCode === 'D' || shiftCode === 'd') {
        return 'Dagvakt';
    }
    if (shiftCode === 'A' || shiftCode === 'a') {
        return 'Kveldsvakt';
    }
    if (shiftCode === 'L' || shiftCode === 'l') {
        return 'Langvakt/Mellomvakt';
    }
    if (shiftCode === 'N' || shiftCode === 'n') {
        return 'Nattevakt';
    }
};

const getErrorMessage = (
    error:
        | {
              data: { body?: { errorCode?: string } } | '';
              config: { url: string };
          }
        | undefined,
): string => {
    if (
        error === undefined ||
        error.data === '' ||
        error.data?.body === undefined
    ) {
        return 'Det oppstod en feil, vennligst prøv igjen senere.';
    }

    if (
        error.config.url.includes('/task/') &&
        error.data.body.errorCode === 'GENERIC_ERROR'
    ) {
        return '';
    }

    switch (error.data.body.errorCode) {
        case 'GENERIC_ERROR':
            return 'Det oppstod en feil, vennligst prøv igjen senere.';
        case 'SAME_NAME_NOT_ALLOWED':
            return 'Dette navnet er allerede brukt. Vennligst velg et annet. ';
        case 'READ_DEPARTMENT_FORBIDDEN':
            return 'Du har ikke tilgang til denne avdelingen.';
        case 'CREATE_DEPARTMENT_LIMIT_REACHED':
            return 'Du kan ikke opprette flere avdelinger, Kontakt support@dynamon dersom du har behov for å opprette flere avdelinger.';
        case 'UPDATE_DEPARTMENT_FORBIDDEN':
            return 'Du har ikke tilgang til å oppdatere denne avdelingen.';
        case 'DEPARTMENT_NOT_FOUND':
            return 'Denne avdelingen finnes ikke.';
        case 'DELETE_DEPARTMENT_NOT_ALLOWED':
            return 'Du kan ikke slette denne avdelingen.';
        case 'READ_EMPLOYEE_FORBIDDEN':
            return 'Du har ikke tilgang til denne ansatte.';
        case 'UPDATE_EMPLOYEE_FORBIDDEN':
            return 'Du har ikke tilgang til å oppdatere denne ansatte.';
        case 'EMPLOYEE_NOT_FOUND':
            return 'Denne ansatte finnes ikke.';
        case 'READ_SHIFT_GENERATOR_TASK_FORBIDDEN':
            return 'Du har ikke tilgang til denne bemanningsplanen.';
        case 'UPDATE_SHIFT_GENERATOR_TASK_FORBIDDEN':
            return 'Du har ikke tilgang til å oppdatere denne bemanningsplanen.';
        case 'SHIFT_GENERATOR_TASK_NOT_FOUND':
            return 'Denne bemanningsplanen finnes ikke.';
        case 'UPDATE_PROCESSED_SHIFT_GENERATOR_TASK_FORBIDDEN':
            return 'Du kan ikke oppdatere en bemanningsplan som holder på å generere.';
        case 'SHIFT_GENERATOR_TASK_INVALID_CONFIG':
            return 'Bemanningsplanen er ugyldig, vennligst fyll inn gyldige verdier i bemanningsplanen og generer';
        case 'READ_SHIFT_GENERATOR_RESULT_FORBIDDEN':
            return 'Du har ikke tilgang til dette resultatet.';
        case 'UPDATE_SHIFT_GENERATOR_RESULT_FORBIDDEN':
            return 'Du har ikke tilgang til å oppdatere dette resultatet.';
        case 'SHIFT_GENERATOR_RESULT_NOT_FOUND':
            return 'Resultat ikke funnet.';
        case 'FORBIDDEN':
            return 'Tilgang forbudt.';
        case 'MISSING_ROLE':
            return 'Bruker mangler roller.';
        case 'USER_NOT_AUTHORIZED':
            return 'Bruker har ikke tilgang.';
        case 'TOKEN_EXPIRED':
            return 'Autentisering utløpt, vennligst logg inn på nytt.';
        case 'EMAIL_NOT_VERIFIED':
            return 'Epostadressen er ikke verifisert, vennligst følg lenken i eposten du mottok for å fortsette.';
        case 'INVALID_CHARACTERS_IN_INPUT':
            return 'Du har brukt noen ugyldige tegn.';
        case 'DELETE_USED_EMPLOYEE_POSITION_FORBIDDEN':
            return 'Kan ikke slette en stilling som er tilknyttet ansatte';
        case 'DELETE_USED_TRAIT_FORBIDDEN':
            return 'Kan ikke slette en spesialkompetanse som er tilknyttet ansatte';
        case 'LICENSE_MAX_EMPLOYEES_REACHED':
            return 'Du har brukt opp ansattkvoten din. Kontakt kundeservice (support@dynamon.no) for å oppgradere lisensen din.';
        case 'LICENSE_MAX_GENERATIONS_REACHED':
            return 'Du har brukt opp genereringskvoten din. Kontakt kundeservice (support@dynamon.no) for å oppgradere lisensen din. ';
        case 'ONLY_ONE_TASK_STARTING_UP_AT_THE_SAME_TIME':
            return 'Du har allerede køet en generering. Vennligst vent til den har startet opp før du starter en ny.';
        case 'ORG_EMPLOYEE_CREATE_FORBIDDEN':
            return 'Du har ikke rettigheter til å knytte ansatte';
        case 'ORG_EMPLOYEE_EMPLOYEE_LIMIT_REACHED':
            return 'Grensen for antall knyttede ansatte er nådd';
        case 'EMPLOYEE_ALREADY_CONNECTED_TO_ORG_EMPLOYEE':
            return 'Den ansatte er allerede knyttet en annen';
        case 'UPDATE_ORG_EMPLOYEE_FORBIDDEN':
            return 'Du har ikke rettigheter til å knytte ansatte';
        case 'READ_ORG_EMPLOYEE_FORBIDDEN':
            return 'Du har ikke rettigheter til å knytte ansatte';
        case 'ORG_EMPLOYEE_SAME_DEPARTMENT_NOT_ALLOWED':
            return 'Ansatte fra samme avdeling kan ikke være knyttet';
        case 'RUNNER_CREATION_FORBIDDEN':
            return 'Du har ikke rettigheter til å opprette en bemanningsplan-liste';
        case 'RUNNER_MAX_SIZE_REACHED':
            return 'Maks grensen for antall knyttede bemanningsplaner, er nådd';
        case 'TASK_ALREADY_CONNECTED_TO_RUNNER':
            return 'En eller flere av de valgte bemanningsplanene er allerede knyttet sammen';
        case 'UPDATE_RUNNER_FORBIDDEN':
            return 'Du har ikke rettigheter til å oppdatere denne bemanningsplan-listen';
        case 'READ_RUNNER_FORBIDDEN':
            return 'Du har ikke rettigheter til å se denne bemanningsplan-listen';
        case 'DEPARTMENT_SHARING_FAILED':
            return 'Deling feilet. Prøv igjen senere';
        case 'ILLEGAL_ARGUMENT':
            return 'Ugyldig input';
        case 'DEPARTMENT_SHARING_MAX_FAILED_ATTEMPTS_REACHED':
            return 'Du har brukt opp kvoten for antall forsøk på deling av avdeling. Vennligst prøv igjen i morgen';
        case 'CREATE_DEPARTMENT_AND_LICENSE_FORBIDDEN':
            return 'Du har brukt opp kvoten for opprettelse av avdelinger. Vennligst kontakt kundeservice';
        case 'ORG_LICENSE_GENERATIONS_LIMIT_REACHED':
            return 'Det er ikke nok genereringer igjen i organisasjonslisensen din. Vennligst kontakt kundeservice';
        case 'ORG_LICENSE_EMPLOYEES_LIMIT_REACHED':
            return 'Det er ikke nok ansatte igjen i organisasjonslisensen din. Vennligst kontakt kundeservice';
        case 'DECREASE_EMPLOYEES_IN_LICENSE_FORBIDDEN':
            return 'Du kan ikke sette ned antall ansatte i avdelingslisensen til et lavere tall enn antall registrerte ansatte i avdelingen';
        case 'DECREASE_GENERATIONS_IN_LICENSE_FORBIDDEN':
            return 'Du kan ikke sette ned antall genereringer i avdelingslisensen til et lavere tall enn antall genereringer brukt i avdelingen';
        case 'LICENSES_CLAIM_IN_TOKEN_INVALID':
            return 'Du har ikke rettigheter til å utføre operasjonen';
        case 'ORG_EMPLOYEE_CROSS_EMAIL_DOMAIN_ATTEMPT':
            return 'Det er kun lov å opprette ansatt-innlogging med samme epost-domene som avdelingsleder';
        default:
            return 'Det oppstod en feil, vennligst prøv igjen senere.';
    }
};

const readOnlyTaskStatuses: Array<string | null | undefined> = [
    'QUEUED',
    'STARTING',
    'PROCESSING',
    'FINISHED',
    'FROZEN',
];

const isTaskReadOnly = (task: TaskStatusSource): boolean =>
    readOnlyTaskStatuses.includes(task?.status);
const isTaskStatusReadOnly = (status: string | null | undefined): boolean =>
    readOnlyTaskStatuses.includes(status);

const isTaskStatusInProcessState = (
    status: string | null | undefined,
): boolean =>
    (
        ['QUEUED', 'STARTING', 'PROCESSING'] as Array<string | null | undefined>
    ).includes(status);

const isTaskInProcessState = (task: TaskStatusSource): boolean =>
    isTaskStatusInProcessState(task?.runner?.status);

const emptyDemand = (
    traits: Record<string, number> = {},
): DailyCoverDemand => ({
    minimumNrOfEmployees: 0,
    maximumNrOfEmployees: 0,
    traitRequirements: traits,
    positionRequirements: {},
});

const SHIFT_TYPE_LIMIT_IN_ORDINARY_BP = 14;
const SHIFT_TYPE_LIMIT_IN_CUSTOM_BP = SHIFT_TYPE_LIMIT_IN_ORDINARY_BP + 4;

const DAY_SEGMENT_NAMES: Record<DaySegment, string> = {
    D: 'Dagvakt',
    A: 'Kveldsvakt',
    N: 'Nattevakt',
    L: 'Langvakt/mellomvakt',
};

const SHIFT_MAX_LOAD_ROLLING_PERIOD_VALUES = [48, 49, 50, 51, 52, 53, 54];

/**
 * Based on the id, returns the norwegian name of the day segment.
 * 0 = Dagvakt
 * 1 = Kveldsvakt
 * 2 = Langvakt/Mellomvakt
 * >3 = Nattevakt
 * @param {number} id
 * @returns {String}
 */
export const dsIdToName = (id: number): string =>
    id === 0
        ? 'Dagvakt'
        : id === 1
          ? 'Kveldsvakt'
          : id === 2
            ? 'Langvakt/Mellomvakt'
            : 'Nattevakt';

export const formatSyntaxError = (
    err: DynomiteError | undefined,
): JSX.Element => {
    if (!err) {
        return <span>Vi kunne ikke validere. Dette er en feil i Dynamon.</span>;
    }
    return (
        <div>
            <span>Vi kunne ikke validere. Dette er en feil i Dynamon.</span>
            <details style={{ border: 'none' }}>
                <summary style={{ fontSize: '14px' }}>detaljer</summary>
                <div
                    style={{
                        background: 'black',
                        color: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                    }}
                />
            </details>
        </div>
    );
};

export const scrollHeight = (timeout = 200): void => {
    setTimeout(() => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth',
        });
    }, timeout);
};

// TODO: This should come from Dynomite, correcting the enum
export enum AvailSource {
    PaidLeave,
    Vacation,
    Weekend,
    Holiday,
    Wish,
    None,
}

export enum RequestStatus {
    Draft,
    Submitted,
    Rejected,
    Approved,
    Modified,
}

export const ALL_AVAIL_SOURCES = [
    AvailSource.PaidLeave,
    AvailSource.Vacation,
    AvailSource.Weekend,
    AvailSource.Holiday,
    AvailSource.Wish,
];

export const ALL_REQUEST_STATUSES = [
    RequestStatus.Draft,
    RequestStatus.Submitted,
    RequestStatus.Rejected,
    RequestStatus.Approved,
    RequestStatus.Modified,
];

export const DAY_SEGMENTS = ['D', 'A', 'N', 'L'] as const;

export {
    debounce,
    taskStuck,
    isTaskInProcessState,
    norwegianShiftNames,
    getErrorMessage,
    isTaskReadOnly,
    isTaskStatusReadOnly,
    emptyDemand,
    SHIFT_TYPE_LIMIT_IN_ORDINARY_BP,
    SHIFT_TYPE_LIMIT_IN_CUSTOM_BP,
    DAY_SEGMENT_NAMES,
    SHIFT_MAX_LOAD_ROLLING_PERIOD_VALUES,
    isTaskStatusInProcessState,
};
